import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import { toast } from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { Upload, Download, Trash2, Paperclip, X } from 'lucide-react';

// ============================================================
// PHASE 4 — DOCUMENTS CARD
// ============================================================
// Reusable component for prospect-shared file uploads on a lead.
// Used on Lead Detail page (Phase 4a). Will also be wired into
// the Add Lead form in Phase 4b (with leadId injected after the
// lead is created).
//
// Props:
//   leadId    (required) — id of the lead the docs belong to
//   leadBdeId (required) — bde_id of the lead (for permission check)
// ============================================================

// Mirror of backend allowlist + limits (defensive client-side validation;
// authoritative check is server-side).
const MAX_FILE_SIZE = 25 * 1024 * 1024;  // 25 MB
const MAX_FILES_PER_UPLOAD = 5;

const ALLOWED_EXTENSIONS = [
  '.pdf', '.doc', '.docx', '.txt',
  '.xls', '.xlsx', '.csv',
  '.ppt', '.pptx',
  '.png', '.jpg', '.jpeg',
  '.zip',
];

// Accept attribute for the file input. Browsers use this for filtering;
// final validation is server-side. Listing extensions is the most robust
// approach across OS file pickers.
const ACCEPT_ATTR = ALLOWED_EXTENSIONS.join(',');

// ============================================================
// Helpers
// ============================================================

const formatFileSize = (bytes) => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const getExtension = (filename) => {
  const idx = (filename || '').lastIndexOf('.');
  return idx >= 0 ? filename.slice(idx).toLowerCase() : '';
};

const DocumentsCard = ({ leadId, leadBdeId }) => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadNote, setUploadNote] = useState('');
  const fileInputRef = useRef(null);

  // Permission helpers — mirror the server-side access matrix.
  const isReviewer = user?.role === 'admin' || user?.role === 'team_lead';
  const canUpload = isReviewer || user?.id === leadBdeId;
  const canDeleteDoc = (doc) => isReviewer || doc.uploaded_by === user?.id;

  useEffect(() => {
    if (!leadId) return;
    fetchDocuments();
  }, [leadId]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/leads/${leadId}/documents`);
      setDocuments(res.data.documents || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load documents.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Client-side validation: count, size, extension. Server is the
    // authoritative validator; this is just for fast feedback.
    if (files.length > MAX_FILES_PER_UPLOAD) {
      toast.error(`Max ${MAX_FILES_PER_UPLOAD} files per upload.`);
      return;
    }
    const tooBig = files.find(f => f.size > MAX_FILE_SIZE);
    if (tooBig) {
      toast.error(`${tooBig.name} is too large (max 25 MB).`);
      return;
    }
    const badExt = files.find(f => !ALLOWED_EXTENSIONS.includes(getExtension(f.name)));
    if (badExt) {
      toast.error(`${badExt.name} has an unsupported type.`);
      return;
    }

    setSelectedFiles(files);
  };

  const clearSelection = () => {
    setSelectedFiles([]);
    setUploadNote('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    const formData = new FormData();
    selectedFiles.forEach(f => formData.append('files', f));
    if (uploadNote.trim()) formData.append('note', uploadNote.trim());

    setUploading(true);
    try {
      const res = await api.post(`/leads/${leadId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(res.data.message || 'Uploaded.');
      clearSelection();
      await fetchDocuments();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await api.get(`/documents/${doc.id}/download`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: doc.mime_type || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Download failed.');
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.file_name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success('Document deleted.');
      await fetchDocuments();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed.');
    }
  };

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h3 style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
        <Paperclip size={16} /> Documents
        {documents.length > 0 && (
          <span style={{ fontSize: 12, color: '#9CA3AF', fontWeight: 400 }}>
            ({documents.length})
          </span>
        )}
      </h3>

      {/* DOC LIST */}
      {loading ? (
        <div style={{ color: '#6B7280', fontSize: 13, padding: '8px 0' }}>Loading documents…</div>
      ) : documents.length === 0 ? (
        <div style={{ color: '#9CA3AF', fontSize: 13, padding: '8px 0' }}>
          No documents yet. {canUpload ? 'Upload prospect-shared files below.' : ''}
        </div>
      ) : (
        <div style={{ marginBottom: canUpload ? 16 : 0 }}>
          {documents.map((doc) => (
            <div
              key={doc.id}
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) auto',
                gap: 12,
                padding: '10px 0',
                borderBottom: '1px solid #F3F4F6',
                alignItems: 'center',
              }}
            >
              <div style={{ overflow: 'hidden' }}>
                <div style={{
                  fontSize: 14, fontWeight: 500, color: '#111827',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {doc.file_name}
                </div>
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
                  {formatFileSize(doc.file_size)} · uploaded by {doc.uploaded_by_name || 'unknown'} · {formatDate(doc.uploaded_at)}
                </div>
                {doc.note && (
                  <div style={{
                    fontSize: 12, color: '#6B7280', marginTop: 4, fontStyle: 'italic',
                    overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    "{doc.note}"
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <button
                  onClick={() => handleDownload(doc)}
                  className="btn btn-secondary"
                  style={{ padding: '4px 8px', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                  title="Download"
                >
                  <Download size={12} /> Download
                </button>
                {canDeleteDoc(doc) && (
                  <button
                    onClick={() => handleDelete(doc)}
                    className="btn btn-secondary"
                    style={{ padding: '4px 8px', fontSize: 12, color: '#DC2626', display: 'inline-flex', alignItems: 'center' }}
                    title="Delete"
                  >
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* UPLOAD WIDGET */}
      {canUpload && (
        <div style={{ borderTop: documents.length > 0 ? '1px solid #E5E7EB' : 'none', paddingTop: documents.length > 0 ? 16 : 0 }}>
          {selectedFiles.length === 0 ? (
            <div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ACCEPT_ATTR}
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <button
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                className="btn btn-secondary"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <Upload size={14} /> Add documents
              </button>
              <span style={{ fontSize: 11, color: '#9CA3AF', marginLeft: 10 }}>
                Up to {MAX_FILES_PER_UPLOAD} files, 25 MB each · PDF, DOCX, XLSX, PPTX, PNG, JPG, CSV, TXT, ZIP
              </span>
            </div>
          ) : (
            <div style={{ background: '#F9FAFB', borderRadius: 6, padding: 12 }}>
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: '#111827', marginBottom: 6 }}>
                  Ready to upload ({selectedFiles.length} file{selectedFiles.length === 1 ? '' : 's'})
                </div>
                {selectedFiles.map((f, i) => (
                  <div key={i} style={{ fontSize: 12, color: '#6B7280', padding: '2px 0' }}>
                    • {f.name} <span style={{ color: '#9CA3AF' }}>({formatFileSize(f.size)})</span>
                  </div>
                ))}
              </div>
              <textarea
                value={uploadNote}
                onChange={(e) => setUploadNote(e.target.value)}
                rows={2}
                placeholder="Optional context note (e.g. 'Scope doc from prospect, May 13')"
                style={{
                  width: '100%', padding: '6px 8px', fontSize: 13,
                  border: '1px solid #D1D5DB', borderRadius: 4, fontFamily: 'inherit',
                  resize: 'vertical', marginBottom: 8,
                }}
                disabled={uploading}
              />
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="btn btn-primary"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                  <Upload size={14} />
                  {uploading ? 'Uploading…' : 'Upload'}
                </button>
                <button
                  onClick={clearSelection}
                  disabled={uploading}
                  className="btn btn-secondary"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                  <X size={14} /> Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DocumentsCard;
